import React, { useState } from 'react';

import { useIntl } from '../../util/reactIntl';
import { submitHubspot } from '../../util/api';

import { H2 } from '../Heading/Heading';
import { PrimaryButton } from '../Button/Button';

import ProgressBar from './ProgressBar';

import css from './ContactGate.module.css';

const CheckboxIcon = ({ checked }) => (
  <svg className={css.checkboxIcon} width="14" height="14" viewBox="0 0 14 14">
    <rect
      x="1"
      y="1"
      width="12"
      height="12"
      rx="2"
      className={checked ? css.checkboxChecked : css.checkboxBox}
    />

    {checked && <path d="M3.5 7.2L6 9.5L10.5 4.5" className={css.checkboxTick} />}
  </svg>
);

const hubspotValues = {
  projectType: {
    residential: 'Hochbau',
    civil: 'Tiefbau',
    infrastructure: 'Infrastruktur',
    renovation: 'Sanierung',
  },

  siteSize: {
    under1000: 'Unter 1.000 m2',
    '1000-5000': '1.000 - 5.000 m2',
    '5000-20000': '5.000 - 20.000 m2',
    over20000: 'Über 20.000 m2',
  },

  materials: {
    cables: 'Kabel',
    diesel: 'Diesel',
    machines: 'Maschinen',
    tools: 'Werkzeuge',
    materials: 'Baumaterial',
    none: 'Sonstiges',
  },

  occupied: {
    always: 'Ja',
    sometimes: 'Manchmal',
    never: 'Nein',
  },

  theftHistory: {
    yes: 'Ja',
    unknown: 'Nicht sicher',
    no: 'Nein',
  },
};

function ContactGate({ answers, onSubmit }) {
  const intl = useIntl();

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const normalizedPhone = phone.replace(/[\s()-]/g, '');

  const validPhone = /^\+?\d{8,15}$/.test(normalizedPhone);

  const handleSubmit = async () => {
    const materials = answers.materials
      .map(value => hubspotValues.materials[value])
      .filter(Boolean)
      .map(value => value.toLowerCase())
      .join(';');

    const body = {
      fields: [
        {
          name: 'email',
          value: email,
        },
        {
          name: 'phone',
          value: phone,
        },
        {
          name: 'projekttyp',
          value: hubspotValues.projectType[answers.projectType],
        },
        {
          name: 'site_size',
          value: hubspotValues.siteSize[answers.siteSize],
        },
        {
          name: 'materialien_auf_der_baustelle',
          value: materials,
        },
        {
          name: 'ist_die_baustelle_nachts_und_am_wochenende_unbesetzt',
          value: hubspotValues.occupied[answers.occupied],
        },
        {
          name: 'diebstahlsereignisse_am_standort',
          value: hubspotValues.theftHistory[answers.theftHistory],
        },
      ],
    };
    if (consent) {
      body.legalConsentOptions = {
        consent: {
          consentToProcess: true,
          text: intl.formatMessage({id: 'RiskCalculator.contact.consent'}),
          communications: [
            {
              value: true,
              subscriptionTypeId: 999,
              text: 'Marketing-Kommunikation',
            },
          ],
        },
      };
    }

    try {
      await submitHubspot(body);
    } catch (err) {
      console.error(err);
    }

    onSubmit({
      email,
      phone,
      consent,
    });
  };

  return (
    <div className={css.root}>
      <div className={css.header}>
        <H2>
          {intl.formatMessage({
            id: 'RiskCalculator.contact.title',
          })}
        </H2>

        <ProgressBar current={5} total={5} />
      </div>

      <div className={css.formPanel}>
        <div className={css.formTitle}>
          {intl.formatMessage({
            id: 'RiskCalculator.contact.readyTitle',
          })}
        </div>

        <p className={css.description}>
          {intl.formatMessage({
            id: 'RiskCalculator.contact.description',
          })}
        </p>

        <div className={css.field}>
          <label className={css.label}>
            {intl.formatMessage({
              id: 'RiskCalculator.contact.emailLabel',
            })}
          </label>

          <input
            className={css.input}
            type="email"
            placeholder={intl.formatMessage({
              id: 'RiskCalculator.contact.email',
            })}
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
        </div>

        <div className={css.field}>
          <label className={css.label}>
            {intl.formatMessage({
              id: 'RiskCalculator.contact.phoneLabel',
            })}
          </label>

          <input
            className={css.input}
            type="tel"
            placeholder={intl.formatMessage({
              id: 'RiskCalculator.contact.phone',
            })}
            value={phone}
            onChange={e => setPhone(e.target.value)}
          />
        </div>

        <label className={css.checkbox}>
          <input
            className={css.checkboxInput}
            type="checkbox"
            checked={consent}
            onChange={e => setConsent(e.target.checked)}
          />

          <CheckboxIcon checked={consent} />

          <span>
            {intl.formatMessage({
              id: 'RiskCalculator.contact.consent',
            })}
          </span>
        </label>

        <PrimaryButton
          className={css.submitButton}
          disabled={!validEmail || !validPhone}
          onClick={handleSubmit}
        >
          {intl.formatMessage({
            id: 'RiskCalculator.contact.submit',
          })}
        </PrimaryButton>
      </div>
    </div>
  );
}

export default ContactGate;
